import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { MenuItem } from '@/types/menu'
import { getMenuTree } from '@/api/system/menu'
import i18n from '@/locales'

/** 递归提取菜单树中所有叶子/节点的 url，用于权限校验 */
export function flattenMenuUrls(items: MenuItem[]): Set<string> {
  const urls = new Set<string>()
  function walk(nodes: MenuItem[]) {
    for (const node of nodes) {
      if (node.path) urls.add(node.path)
      if (node.children?.length) walk(node.children)
    }
  }
  walk(items)
  return urls
}

/**
 * 菜单树中是否存在归属于某一顶层路径段的页面（如 `earn` 对应 `/earn/products`）。
 * 分组节点 path 常为空串、按钮项不占菜单位置，均不计入。
 *
 * 各站点开放哪些模块由后端菜单下发决定，前端据此判断某业务在当前站点是否露出。
 */
export function hasVisibleMenuSection(items: MenuItem[], section: string): boolean {
  const prefix = `/${section}/`
  function walk(nodes: MenuItem[]): boolean {
    return nodes.some(
      (node) =>
        (node.category !== 'button' &&
          node.is_visible !== false &&
          node.path?.startsWith(prefix)) ||
        (node.children?.length ? walk(node.children) : false)
    )
  }
  return walk(items)
}

/** 递归过滤掉 is_visible 为 false 的节点 */
function filterVisible(items: MenuItem[]): MenuItem[] {
  return items
    .filter((item) => item.is_visible !== false)
    .map((item) => ({
      ...item,
      children: item.children?.length ? filterVisible(item.children) : undefined,
    }))
}

interface MenuState {
  items: MenuItem[]
  loading: boolean
  error: string | null
  /** 本次会话是否已从服务端加载完成（不持久化，刷新后重置为 false） */
  initialized: boolean
}

export interface FetchMenusOptions {
  /**
   * 静默刷新：不切换 loading、失败时保留当前菜单。AppLayout 在 loading 期间会卸载整个布局，
   * 页面内发起的刷新（如切换模块开关后同步侧栏）必须走静默模式，否则当前页会被卸载重挂。
   */
  silent?: boolean
}

interface MenuActions {
  fetchMenus(options?: FetchMenusOptions): Promise<void>
  setItems(items: MenuItem[]): void
  clearMenus(): void
}

type MenuStore = MenuState & MenuActions

const useMenuStore = create<MenuStore>()(
  persist(
    (set) => ({
      items: [],
      loading: false,
      error: null,
      initialized: false,

      async fetchMenus(options?: FetchMenusOptions) {
        if (options?.silent) {
          try {
            const items = await getMenuTree()
            set({ items: filterVisible(items) })
          } catch {
            // 请求层已弹出错误提示；保留当前菜单，下次整页加载时再同步
          }
          return
        }
        // 每次从服务端拉取前清除旧缓存，确保 isVisible 过滤生效
        sessionStorage.removeItem('menu-storage')
        set({ loading: true, error: null })
        try {
          const items = await getMenuTree()
          set({ items: filterVisible(items), loading: false, initialized: true })
        } catch (err: unknown) {
          const message =
            err && typeof err === 'object' && 'message' in err
              ? String((err as { message: unknown }).message)
              : i18n.t('nav.loadFailed')
          set({ error: message, loading: false, initialized: true })
        }
      },

      setItems(items: MenuItem[]) {
        set({ items })
      },

      clearMenus() {
        set({ items: [], error: null, initialized: false })
      },
    }),
    {
      name: 'menu-storage',
      storage: createJSONStorage(() => sessionStorage),
      // initialized 不持久化，每次刷新都重新从服务端加载
      partialize: (state) => ({ items: state.items }),
    }
  )
)

export default useMenuStore
